// Pure, framework-agnostic unit-calculation logic shared by the accessorial
// config modal (tools/AccessorialForm) and the accessorial entry UI.

import moment from 'moment';

export const CALENDAR_DAYS = 'calendar_days';
export const ELAPSED_DECIMAL_DAYS = 'elapsed_decimal_days';
export const ELAPSED_DECIMAL_HOURS = 'elapsed_decimal_hours';
export const ELAPSED_WHOLE_HOURS = 'elapsed_whole_hours';

export const SHIPPER_CARRIER = 'shipper_carrier';
export const SHIPPER_ONLY = 'shipper_only';
export const CARRIER_ONLY = 'carrier_only';

export const appliesToOptions = [
  { label: 'Shipper + Carrier', value: SHIPPER_CARRIER },
  { label: 'Shipper only', value: SHIPPER_ONLY },
  { label: 'Carrier only', value: CARRIER_ONLY },
];

// The calculation method is fully determined by the Rate Type + Unit Type
// combination, so it is shown read-only rather than as an editable dropdown.
export function getCalcMethodOptions(rateType, unitType) {
  // Legacy accessorials may still carry 'integer'; treat it as 'number'.
  const normalizedUnitType = unitType === 'integer' ? 'number' : unitType;

  if (rateType === 'daily' && normalizedUnitType === 'number') {
    return [{ value: CALENDAR_DAYS, label: 'Calendar days' }];
  }
  if (rateType === 'daily' && normalizedUnitType === 'decimal') {
    return [{ value: ELAPSED_DECIMAL_DAYS, label: 'Elapsed decimal days' }];
  }
  if (rateType === 'hourly' && normalizedUnitType === 'decimal') {
    return [{ value: ELAPSED_DECIMAL_HOURS, label: 'Elapsed decimal hours' }];
  }
  if (rateType === 'hourly' && normalizedUnitType === 'number') {
    return [{ value: ELAPSED_WHOLE_HOURS, label: 'Elapsed whole hours' }];
  }
  return [];
}

// Illustrative preview copy keyed by calculation method.
export const CALC_METHOD_PREVIEW = {
  [CALENDAR_DAYS]: {
    example: 'May 15, 2026 → May 16, 2026 = 2 units',
    description: 'Counts each calendar date from Begin Date through End Date.',
  },
  [ELAPSED_DECIMAL_DAYS]: {
    example: '30 hours = 1.2500 units',
    description: 'Calculates exact elapsed time and converts it to days.',
  },
  [ELAPSED_DECIMAL_HOURS]: {
    example: '90 minutes = 1.50 units',
    description: 'Calculates exact elapsed time in hours.',
  },
  [ELAPSED_WHOLE_HOURS]: {
    example: '90 minutes = 2 units',
    description: 'Partial hours are counted as a full hour.',
  },
};

// Keeps calc_method consistent with the current Rate Type + Unit Type. If the
// stored method is no longer valid for the combination, fall back to the only
// option the combination allows (or null when none applies, e.g. Flat).
export function reconcileCalcMethod(accessorial) {
  const options = getCalcMethodOptions(
    accessorial.rate_type,
    accessorial.unit_type,
  );
  const stillValid = options.some((o) => o.value === accessorial.calc_method);
  return {
    ...accessorial,
    calc_method: stillValid
      ? accessorial.calc_method
      : (options[0]?.value ?? null),
  };
}

// Visibility states for the Unit Calculation section.
export const UNIT_CALC_HIDDEN = 'hidden';
export const UNIT_CALC_PROMPT = 'prompt';
export const UNIT_CALC_AVAILABLE = 'available';

// Derives the Unit Calculation view-model from the accessorial config. Pure so
// it can be unit-tested and reused outside React.
export function getUnitCalculationView({
  rate_type,
  unit_type,
  begin_date_required,
  end_date_required,
  calc_method,
}) {
  // Flat rate types never support Begin/End unit calculation.
  if (rate_type === 'flat') return { mode: UNIT_CALC_HIDDEN };

  const bothDates = begin_date_required && end_date_required;
  if (!bothDates) {
    // Only nudge when exactly one of the two dates is enabled; hide otherwise.
    if (begin_date_required || end_date_required) {
      return {
        mode: UNIT_CALC_PROMPT,
        missingDateField: begin_date_required
          ? 'end_date_required'
          : 'begin_date_required',
      };
    }
    return { mode: UNIT_CALC_HIDDEN };
  }

  const calcMethodOptions = getCalcMethodOptions(rate_type, unit_type);
  const selectedMethod =
    calcMethodOptions.find((o) => o.value === calc_method) ??
    calcMethodOptions[0] ??
    null;

  return {
    mode: UNIT_CALC_AVAILABLE,
    calcMethodOptions,
    selectedMethod,
    preview: selectedMethod ? CALC_METHOD_PREVIEW[selectedMethod.value] : null,
    showDecimalPlaces: unit_type === 'decimal',
  };
}

// ---------------------------------------------------------------------------
// Accessorial entry UI ("apply changes") — auto-calculate units
// ---------------------------------------------------------------------------

// i18n key for the End-before-Begin error shown in the entry UI.
export const END_BEFORE_BEGIN_ERROR = 'END_DATE_CANNOT_BE_BEFORE_BEGIN_DATE';

// Calendar-day methods compare dates only; the elapsed methods use time.
export function methodUsesTime(calcMethod) {
  return calcMethod !== CALENDAR_DAYS;
}

// Which unit fields Applies To populates. Unlisted fields stay manual.
export function getAppliesToFields(appliesTo) {
  return {
    shipper: appliesTo === SHIPPER_CARRIER || appliesTo === SHIPPER_ONLY,
    carrier: appliesTo === SHIPPER_CARRIER || appliesTo === CARRIER_ONLY,
  };
}

function toDecimalPlaces(decimalPlaces, fallback) {
  const parsed = Number(decimalPlaces);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

function applyMinimum(units, minimumUnits) {
  const min = Number(minimumUnits);
  if (Number.isFinite(min) && min > units) return min;
  return units;
}

/**
 * Computes the number of units for a calc method and a Begin/End range. Pure:
 * returns a finite number, or `null` when the inputs can't produce a value
 * (missing date, invalid date, or End before Begin). Callers decide what to do
 * with `null` (leave the field untouched / surface the date error separately).
 */
export function calculateUnits({
  calcMethod,
  beginDate,
  endDate,
  decimalPlaces,
  minimumUnits,
}) {
  if (!beginDate || !endDate) return null;
  const begin = moment(beginDate);
  const end = moment(endDate);
  if (!begin.isValid() || !end.isValid()) return null;
  if (end.isBefore(begin)) return null;

  let units;
  switch (calcMethod) {
    case CALENDAR_DAYS:
      // Inclusive count of calendar dates; time of day is ignored.
      units =
        end.clone().startOf('day').diff(begin.clone().startOf('day'), 'days') +
        1;
      break;
    case ELAPSED_DECIMAL_DAYS: {
      const hours = end.diff(begin, 'minutes') / 60;
      const places = toDecimalPlaces(decimalPlaces, 4);
      units = Number((hours / 24).toFixed(places));
      break;
    }
    case ELAPSED_DECIMAL_HOURS: {
      const minutes = end.diff(begin, 'minutes');
      const places = toDecimalPlaces(decimalPlaces, 2);
      units = Number((minutes / 60).toFixed(places));
      break;
    }
    case ELAPSED_WHOLE_HOURS: {
      const minutes = end.diff(begin, 'minutes');
      // Any partial hour counts as a full hour.
      units = Math.ceil(minutes / 60);
      break;
    }
    default:
      return null;
  }

  return applyMinimum(units, minimumUnits);
}

/**
 * High-level helper for the entry UI. Given an accessorial config and the
 * entered Begin/End dates, returns:
 *   - `active`  : whether auto-calculate should drive any unit fields,
 *   - `fields`  : which unit fields it owns (read-only) per Applies To,
 *   - `units`   : the calculated value (or null when not yet computable),
 *   - `error`   : i18n key for an invalid range (End before Begin), else null.
 *
 * `active` only reflects the config (auto_calculate_units + a usable method),
 * so the consumer can mark fields read-only before both dates are entered.
 */
export function getAutoUnitCalculation(accessorial, { beginDate, endDate }) {
  const {
    auto_calculate_units,
    calc_method,
    applies_to,
    decimal_places,
    minimum_units,
  } = accessorial || {};

  const fields = getAppliesToFields(applies_to);
  const active = !!auto_calculate_units && !!calc_method;

  if (!active) {
    return { active: false, fields, units: null, error: null };
  }

  let error = null;
  if (beginDate && endDate) {
    const begin = moment(beginDate);
    const end = moment(endDate);
    if (begin.isValid() && end.isValid() && end.isBefore(begin)) {
      error = END_BEFORE_BEGIN_ERROR;
    }
  }

  const units = calculateUnits({
    calcMethod: calc_method,
    beginDate,
    endDate,
    decimalPlaces: decimal_places,
    minimumUnits: minimum_units,
  });

  return { active: true, fields, units, error };
}
